import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

function CreateBulkOrder() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const fileInputRef = useRef(null);
  const [latestOrder, setLatestOrder] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");

    if (!authToken || !merchantId) {
      console.error("Auth token or merchant id not found in local storage.");
      navigate("/login");
      return;
    }

    fetchUploadHistory(authToken, merchantId);
  }, [navigate]);

  // Calculate statistics
  const statistics = {
    totalUploads: uploadHistory.length,
    totalOrders: uploadHistory.reduce((sum, file) => sum + (file.total_orders || 0), 0),
    successfulOrders: uploadHistory.reduce((sum, file) => sum + (file.successful_orders || 0), 0),
    failedOrders: uploadHistory.reduce((sum, file) => sum + (file.failed_orders || 0), 0),
  };

  const fetchUploadHistory = async (authToken, merchantId) => {
    console.log("Fetching upload history for merchant:", merchantId);
    const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_GET_BULK_ORDER_FILE_DETAILS}${merchantId}`;

    console.log("API URL:", apiUrl);
    setIsLoading(true);
    try {
      const response = await axios.get(
        apiUrl,
        {
          headers: {
            Authorization: authToken,
            "x-merchant-id": merchantId,
          },
        }
      );
      setUploadHistory(response.data);
    } catch (err) {
      console.error("Error fetching upload history:", err);
      toast.error("Failed to fetch upload history");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshFileUrl = async (transactionId) => {
    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
  
    if (!authToken || !merchantId) {
      console.error("Auth token or merchant ID not found in local storage.");
      navigate("/login");
      return;
    }
  
    try {
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}/refresh-file-url/${transactionId}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: authToken,
          "x-merchant-id": merchantId,
        },
      });
  
      if (response.status === 200) {
        toast.success("File URL refreshed successfully!");
        
        setUploadHistory((prevHistory) =>
          prevHistory.map((file) =>
            file.bulk_order_file_transaction_id === transactionId
              ? { ...file, bulk_order_request_file_s3_url: response.data.new_file_url }
              : file
          )
        );
      }
    } catch (err) {
      console.error("Error refreshing file URL:", err);
      toast.error("Failed to refresh file URL");
    }
  };
  

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type !== "text/csv") {
      toast.error("Please upload a CSV file.");
      setFile(null);
    } else {
      setFile(selectedFile || null);
      if (selectedFile) {
        toast.success(`File "${selectedFile.name}" selected successfully`);
      }
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type !== "text/csv") {
      toast.error("Please upload a CSV file.");
      setFile(null);
    } else {
      setFile(droppedFile || null);
      if (droppedFile) {
        toast.success(`File "${droppedFile.name}" selected successfully`);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      toast.error("No file selected or file type is invalid.");
      return;
    }

    const formData = new FormData();
    formData.append("bulk_order_file", file);
    const transactionId = uuidv4();

    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");

    if (!authToken || !merchantId) {
      console.error("Auth token or merchant ID not found in local storage.");
      navigate("/login");
      return;
    }

    setIsUploading(true);
    try {
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_BULK_FORWARD_ORDER_ENDPOINT}`;
      const response = await axios.post(apiUrl, formData, {
        headers: {
          Authorization: authToken,
          isSync: "true",
          transactionId: transactionId,
          "x-merchant-id": merchantId,
        },
      });

      if (response.status === 200) {
        toast.success("Order submitted successfully!");
        setLatestOrder(response.data || null);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        fetchUploadHistory(authToken, merchantId);
      }
    } catch (err) {
      console.error("Error creating bulk order:", err);
      toast.error(err.response?.data?.message || "Failed to create bulk order");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSampleDownload = () => {
    const link = document.createElement("a");
    link.href = "/sample-bulk-order.csv";
    link.setAttribute("download", "sample-bulk-order.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Sample file downloaded successfully");
  };

  // Filter and search functionality
  const filteredHistory = uploadHistory
    .filter((file) => {
      const matchesSearch = file.bulk_order_file_transaction_id
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || file.file_processing_status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.created_on) - new Date(a.created_on));

  // Pagination
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const paginatedHistory = filteredHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      order_processing_completed: {
        bg: "bg-green-100",
        text: "text-green-800",
        icon: "✓",
        label: "Completed",
      },
      processing: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: "⟳",
        label: "Processing",
      },
      failed: {
        bg: "bg-red-100",
        text: "text-red-800",
        icon: "✕",
        label: "Failed",
      },
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        icon: "⏱",
        label: "Pending",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}
      >
        <span>{config.icon}</span>
        {config.label}
      </span>
    );
  };

  // View details modal
  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">Order Details</h2>
                <p className="text-indigo-100 text-sm">Transaction ID: {order.bulk_order_file_transaction_id}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Status Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">Processing Status</h3>
              <StatusBadge status={order.file_processing_status} />
            </div>

            {/* Order Statistics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{order.total_orders || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Total Orders</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{order.successful_orders || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Successful</div>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-red-600">{order.failed_orders || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Failed</div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="space-y-4">
              <div className="border-b pb-3">
                <div className="text-sm text-gray-500">Created On</div>
                <div className="font-medium text-gray-900">{new Date(order.created_on).toLocaleString()}</div>
              </div>

              {order.username && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500">Username</div>
                  <div className="font-medium text-gray-900">{order.username}</div>
                </div>
              )}

              {order.current_processing_order_id && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500">Current Processing Order ID</div>
                  <div className="font-medium text-gray-900 break-all">{order.current_processing_order_id}</div>
                </div>
              )}

              {order.awb_number && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500">AWB Number</div>
                  <div className="font-medium text-gray-900">{order.awb_number}</div>
                </div>
              )}

              {order.order_sequence_number && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500">Order Sequence Number</div>
                  <div className="font-medium text-gray-900">{order.order_sequence_number}</div>
                </div>
              )}

              {order.bulk_order_request_file_s3_url && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500 mb-2">Request File</div>
                  <a
                    href={order.bulk_order_request_file_s3_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                    </svg>
                    Download Request File
                  </a>
                </div>
              )}

              {order.bulk_order_response_file_s3_url && (
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-500 mb-2">Response File</div>
                  <a
                    href={order.bulk_order_response_file_s3_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z" />
                    </svg>
                    Download Response File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 px-3">
      {/* Modal */}
      {showModal && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowModal(false);
            setSelectedOrder(null);
          }}
        />
      )}

      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-2">
          <div>
            <h3 className="text-xl font-bold text-gray-900">📦 Bulk Order Management</h3>
            <p className="text-gray-600">Upload and track your bulk orders efficiently</p>
          </div>
          <button
            onClick={handleSampleDownload}
            className="inline-flex items-center gap-2 bg-white text-indigo-600 border-2 border-indigo-600 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-all shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Sample CSV
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 border-l-4 border-indigo-500 transform hover:scale-105 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Total Uploads</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{statistics.totalUploads}</p>
              </div>
              <div className="bg-indigo-100 p-2 md:p-3 rounded-xl">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 border-l-4 border-blue-500 transform hover:scale-105 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{statistics.totalOrders}</p>
              </div>
              <div className="bg-blue-100 p-2 md:p-3 rounded-xl">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 border-l-4 border-green-500 transform hover:scale-105 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Successful Orders</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{statistics.successfulOrders}</p>
              </div>
              <div className="bg-green-100 p-2 md:p-3 rounded-xl">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 border-l-4 border-red-500 transform hover:scale-105 transition-all">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-gray-600">Failed Orders</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-1 md:mt-2">{statistics.failedOrders}</p>
              </div>
              <div className="bg-red-100 p-2 md:p-3 rounded-xl">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-2xl shadow-xl p-5 md:p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Bulk Order File
          </h3>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept=".csv"
            disabled={isUploading}
          />

          <div
            className={`relative border-3 border-dashed rounded-2xl p-8 md:p-10 text-center cursor-pointer transition-all ${
              isDragging
                ? "border-indigo-500 bg-indigo-50 scale-105"
                : file
                ? "border-green-400 bg-green-50"
                : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
            }`}
            onClick={handleUploadClick}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-lg font-semibold text-indigo-600">Uploading...</p>
                <p className="text-sm text-gray-600">Please wait while we process your file</p>
              </div>
            ) : file ? (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {(file.size / 1024).toFixed(2)} KB • CSV File
                  </p>
                </div>
                <p className="text-sm text-green-600 font-medium">✓ Ready to upload</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    Drop your CSV file here or click to browse
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Supported format: CSV (Comma Separated Values)
                  </p>
                </div>
              </div>
            )}
          </div>

          {file && (
            <div className="mt-4 md:mt-6 flex gap-3 md:gap-4 justify-center flex-wrap">
              <button
                onClick={handleSubmit}
                disabled={isUploading}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {isUploading ? "Uploading..." : "Upload & Process"}
              </button>
              <button
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                disabled={isUploading}
                className="inline-flex items-center gap-2 bg-white text-gray-700 border-2 border-gray-300 px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Latest Order Success Message */}
        {latestOrder && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 md:p-6 shadow-lg animate-pulse">
            <div className="flex items-start gap-4">
              <div className="bg-green-500 text-white p-3 rounded-full">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-green-900 mb-2">✓ Latest Order Submitted Successfully!</h3>
                <div className="space-y-2 text-sm">
                  <p className="text-gray-700">
                    <span className="font-semibold">Transaction ID:</span>{" "}
                    <span className="font-mono text-gray-900">{latestOrder.bulk_order_file_transaction_id}</span>
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Status:</span>{" "}
                    <StatusBadge status={latestOrder.file_processing_status} />
                  </p>
                </div>
                {latestOrder.bulk_order_request_file_s3_url && (
                  <a
                    href={latestOrder.bulk_order_request_file_s3_url}
                    className="inline-flex items-center gap-2 mt-4 text-indigo-600 font-semibold hover:text-indigo-800 transition-all"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Uploaded File
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload History Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 md:p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Upload History
              </h3>

              <div className="flex flex-col md:flex-row gap-3">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by Transaction ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-10 pr-4 py-2 border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all w-full md:w-64"
                  />
                  <svg
                    className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="order_processing_completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">Loading upload history...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No uploads found</h3>
              <p className="text-gray-600">
                {searchTerm || filterStatus !== "all"
                  ? "Try adjusting your search or filter"
                  : "Start by uploading your first bulk order file"}
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Transaction ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Upload Date
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Success
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Failed
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedHistory.map((file, index) => (
                      <tr
                        key={index}
                        className="hover:bg-indigo-50 transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedOrder(file);
                          setShowModal(true);
                        }}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {file.bulk_order_file_transaction_id.substring(0, 8)}...
                          </div>
                          <div className="text-xs text-gray-500">
                            {file.username || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={file.file_processing_status} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(file.created_on).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(file.created_on).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 rounded-lg font-bold text-sm">
                            {file.total_orders || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-green-100 text-green-800 rounded-lg font-bold text-sm">
                            {file.successful_orders || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-red-100 text-red-800 rounded-lg font-bold text-sm">
                            {file.failed_orders || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            {file.bulk_order_request_file_s3_url && (
                              <>
                                <a
                                  href={file.bulk_order_request_file_s3_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-all"
                                  title="View File"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </a>
                                <button
                                  onClick={() => refreshFileUrl(file.bulk_order_file_transaction_id)}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                  title="Refresh URL"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 md:px-6 py-3 md:py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
                      <span className="font-semibold">
                        {Math.min(currentPage * itemsPerPage, filteredHistory.length)}
                      </span>{" "}
                      of <span className="font-semibold">{filteredHistory.length}</span> results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Previous
                      </button>
                      <div className="flex gap-1">
                        {[...Array(totalPages)].map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                              currentPage === i + 1
                                ? "bg-indigo-600 text-white"
                                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 bg-white border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateBulkOrder;
